import { type CSSProperties, useLayoutEffect, useRef } from "react";
import Swiper from "@/components/shared/Swiper";
import { SWIPER_SLIDE } from "@/components/shared/swiperSlideClass";
import { eventNames } from "@/modules/Constants";
import type { ExperienceState } from "@/types/state";

import CloseXIcon from "@svg/close_x.svg";

const REGION = "Favorites Header";

type FavoritesHeaderProps = {
  experienceState: ExperienceState;
  onApplicationEvent: (event: Record<string, unknown>) => void;
  onFavoriteSelected: (region: string, index: number) => void;
};

export default function FavoritesHeader({
  experienceState,
  onApplicationEvent,
  onFavoriteSelected,
}: FavoritesHeaderProps) {
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const cursorRef = useRef<HTMLDivElement>(null);

  // Runs after every render, before paint, so the cursor never lags the layout.
  useLayoutEffect(() => {
    const cursor = cursorRef.current;
    if (cursor) {
      const index = experienceState.favoritesData.favoriteIndex;
      const item = itemsRef.current[index] as HTMLDivElement;
      const button = item.childNodes[0] as HTMLElement | undefined;

      const itemOffset = item ? item.offsetLeft : 0;
      const offset = button ? button.offsetLeft : 0;
      const size = button ? button.offsetWidth : 10;
      const tr = `translate(${itemOffset + offset}px, 0)`;
      cursor.style.width = `${size}px`;
      cursor.style.height = "";
      cursor.style.transform = tr;
      cursor.style.webkitTransform = tr;
    }
  });

  const getFavoritesList = () => {
    const experienceData = experienceState.experienceData;
    const favoritesData = experienceState.favoritesData;
    const favorites = favoritesData.getFavorites();
    const index = favoritesData.favoriteIndex;

    const list = [];
    const max = favorites.length - 1;

    for (let n = 0; n <= max; n++) {
      const imageUrl = experienceData.getArticleImageUrlById(favorites[n]);
      const style: CSSProperties = {};

      if (imageUrl) {
        style.backgroundImage = `url(${imageUrl})`;
      }

      const div =
        n < max ? (
          <div
            key={`favorite-picker-separator-${n}`}
            className="separator portrait tw:mt-[7px] tw:ml-[5px] tw:h-[41px] tw:w-[2px] tw:bg-[rgba(200,200,200,0.5)]"
          />
        ) : null;

      list.push(
        <div
          ref={item => {
            itemsRef.current[n] = item;
          }}
          key={`favorite-picker-key-${n}`}
          className={`${SWIPER_SLIDE} tw:flex-row tw:items-center tw:text-center tw:[background:#fff]`}
        >
          <button
            key={`favorite-picker-image-${n}`}
            className="button hover-bottom tw:ml-[5px] tw:h-[55px] tw:w-[45px]"
            aria-label={
              experienceData.getArticleById(favorites[n])?.title ||
              `Favorite ${n + 1}`
            }
            onClick={() => onFavoriteSelected(REGION, n)}
          >
            <div
              className="tw:mt-[1px] tw:h-[45px] tw:w-[45px] tw:bg-center tw:bg-contain tw:bg-no-repeat"
              style={style}
            />
          </button>
          {div}
        </div>,
      );
    }

    if (favorites.length > 0 && index >= 0) {
      list.push(
        <div
          ref={cursorRef}
          key="favorite-cursor"
          className="cursor highlight-bg-1 tw:absolute tw:bottom-0 tw:left-0 tw:h-[2px] tw:w-[45px]"
        />,
      );
    }

    return list;
  };

  return (
    <div className="favorites-header tw:relative tw:h-[55px] tw:w-full tw:overflow-hidden tw:bg-[#fff]">
      <Swiper
        className="favorites-picker"
        config={{
          slidesPerView: "auto",
          freeMode: true,
        }}
      >
        {getFavoritesList()}
      </Swiper>
      <button
        className="favorites-dismiss hover type-3 button square tw:absolute tw:top-[10px] tw:right-0"
        aria-label="Close favorites"
        onClick={() =>
          onApplicationEvent({
            name: eventNames.DISMISS_FAVORITES,
            region: REGION,
          })
        }
      >
        <CloseXIcon className="centered icon-close-x-d" />
      </button>
    </div>
  );
}
